import React from 'react';
import { FloorplanData, FloorplanElement } from './types';

interface FloorplanRendererProps {
  floorplan: FloorplanData;
}

export function FloorplanRenderer({ floorplan }: FloorplanRendererProps) {
  const renderElement = (element: FloorplanElement, index: number) => {
    switch (element.type) {
      case 'wall':
        return (
          <rect
            key={`wall-${index}`}
            x={element.x}
            y={element.y}
            width={element.width}
            height={element.height}
            fill="#8b7355"
            stroke="#6b5d4f"
            strokeWidth={1}
          />
        );

      case 'door':
        return (
          <g key={`door-${index}`}>
            <rect
              x={element.x}
              y={element.y}
              width={element.width}
              height={element.height}
              fill="#d4a574"
              stroke="#8b7355"
              strokeWidth={1}
            />
            <path
              d={
                element.orientation === 'horizontal'
                  ? `M ${element.x} ${element.y} L ${element.x + element.width} ${element.y}`
                  : `M ${element.x} ${element.y} L ${element.x} ${element.y + element.height}`
              }
              stroke="#6b5d4f"
              strokeWidth={2}
              fill="none"
              strokeDasharray="4,2"
            />
          </g>
        );

      case 'room':
        const hasNoStroke = element.stroke === 'none' || element.stroke === 'transparent';
        const rectProps: React.SVGProps<SVGRectElement> = {
          x: element.x,
          y: element.y,
          width: element.width,
          height: element.height,
          fill: element.fill || '#ffffff',
          className: 'opacity-30 transition-opacity duration-200',
        };
        
        if (!hasNoStroke) {
          rectProps.stroke = element.stroke || '#cccccc';
          rectProps.strokeWidth = 1;
        } else {
          rectProps.stroke = 'none';
          rectProps.strokeWidth = 0;
        }
        
        return (
          <g key={`room-${index}`}>
            <rect {...rectProps} />
            {element.label && (
              <text
                x={element.labelX || element.x + element.width / 2}
                y={element.labelY || element.y + element.height / 2}
                textAnchor="middle"
                dominantBaseline="middle"
                className="pointer-events-none select-none"
                fontSize={12}
                fontWeight="600"
                fill="#666"
              >
                {element.label}
              </text>
            )}
          </g>
        );

      case 'column':
        return (
          <rect
            key={`column-${index}`}
            x={element.x}
            y={element.y}
            width={element.width}
            height={element.height}
            fill="#6b5d4f"
            stroke="#4a3d2f"
            strokeWidth={1}
          />
        );

      case 'label':
        return (
          <text
            key={`label-${index}`}
            x={element.x}
            y={element.y}
            fontSize={element.fontSize || 12}
            fontWeight={element.fontWeight || '400'}
            fill={element.fill || '#333'}
            textAnchor="middle"
            dominantBaseline="middle"
            className="pointer-events-none select-none"
          >
            {element.text}
          </text>
        );

      case 'path':
        return (
          <path
            key={`path-${index}`}
            d={element.pathD}
            stroke={element.stroke || '#999'}
            strokeWidth={element.strokeWidth || 1}
            fill={element.fill || 'none'}
            className="opacity-50"
          />
        );

      default:
        return null;
    }
  };

  return (
    <g className="pointer-events-none">
      {floorplan.elements.map((element, index) => renderElement(element, index))}
    </g>
  );
}

